import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { BrowserLocation, Step } from '../types'
import { type EntrySourceMessage, GeolocationPermission, type SetLocationMessage } from '../types'
import { useAuth } from '@/tools/auth'
import { glovoRum, useErrorHandling } from '@/tools/o11y/client'
import type { AddressCoordinates, UserAddressNew } from '@/user/address-book/entities'
import { getAddressByCoordinates, isLocationInDeliveryArea } from '@/user/address-book/use-cases'

type Props = {
  browserLocation: BrowserLocation
  setBrowserLocation: Dispatch<SetStateAction<BrowserLocation>>
  currentStep: Step
}

export const useCurrentLocation = ({ browserLocation, setBrowserLocation, currentStep }: Props) => {
  const { isLoggedIn } = useAuth()
  const [verifiedBrowserAddress, setVerifiedBrowserAddress] = useState<UserAddressNew | null>(null)
  const [isLocationBeingResolved, setIsLocationBeingResolved] = useState(false)

  const { execWithErrorTracking } = useErrorHandling()

  const permission = useRef<PermissionStatus>(null)

  const [hasReceivedBrowserPermission, setHasReceivedBrowserPermission] = useState(false)

  const verifyAndCalculateAddress = useCallback(
    async (coordinates: AddressCoordinates) => {
      const isInDeliveryArea = await isLocationInDeliveryArea(coordinates)
      if (!isInDeliveryArea) {
        return null
      }
      return await getAddressByCoordinates(coordinates, isLoggedIn)
    },
    [isLoggedIn]
  )

  function getBrowserLocation(
    successCallback?: (coords: GeolocationCoordinates) => void,
    errorCallback?: () => void
  ) {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setBrowserLocation({
          coordinates: coords,
          geolocationPermission: GeolocationPermission.GRANTED,
        })
        successCallback && successCallback(coords)
        setIsLocationBeingResolved(false)
      },
      () => {
        setBrowserLocation({
          coordinates: null,
          geolocationPermission: GeolocationPermission.DENIED,
        })
        errorCallback && errorCallback()
        setIsLocationBeingResolved(false)
      }
    )
  }

  const requestLocation = (
    successCallback?: (coords?: GeolocationCoordinates) => void,
    errorCallback?: () => void
  ) => {
    setIsLocationBeingResolved(true)
    if (!navigator?.permissions?.query) {
      setBrowserLocation({
        coordinates: null,
        geolocationPermission: GeolocationPermission.UNSUPPORTED,
      })
      glovoRum.addAction('user.address_book.root.unsupported_browser_location_api', {
        userAgent: navigator?.userAgent || '__UNKNOWN__',
      })
      return
    }

    void navigator.permissions.query({ name: 'geolocation' }).then((result: PermissionStatus) => {
      if (result.state === GeolocationPermission.GRANTED) {
        getBrowserLocation(successCallback, errorCallback)
      } else if (result.state === GeolocationPermission.PROMPT) {
        navigator.geolocation.getCurrentPosition(
          () => undefined,
          () => undefined
        ) // Only to show browser popup to request permission
        permission.current = result
        result.onchange = () => {
          getBrowserLocation(successCallback, errorCallback)
        }
      } else if (result.state === GeolocationPermission.DENIED) {
        setBrowserLocation({
          coordinates: null,
          geolocationPermission: GeolocationPermission.DENIED,
        })
        errorCallback && errorCallback()
        setIsLocationBeingResolved(false)
      }
    })
  }

  const requestLocationIfPossible = () => {
    if (
      browserLocation.geolocationPermission !== GeolocationPermission.UNSUPPORTED &&
      browserLocation.geolocationPermission !== GeolocationPermission.DENIED &&
      browserLocation.geolocationPermission !== GeolocationPermission.GRANTED &&
      currentStep !== 'FIND_ADDRESS_BY_TEXT'
    ) {
      requestLocation()
    }
  }

  /**
   *
   * TODO: Delete this function once AddressBook is not consumed anymore from an Iframe
   *
   */
  function onParentMessage(message: SetLocationMessage | EntrySourceMessage) {
    if (message.data.type === 'ADDRESS_BOOK.SET_LOCATION' && message.data.payload) {
      setBrowserLocation({
        coordinates: message.data.payload,
        geolocationPermission: GeolocationPermission.GRANTED,
      })
      setHasReceivedBrowserPermission(true)
    }
  }

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (
        !browserLocation?.coordinates ||
        browserLocation?.geolocationPermission !== GeolocationPermission.GRANTED
      ) {
        return
      }
      const address = await verifyAndCalculateAddress(browserLocation.coordinates)
      setVerifiedBrowserAddress(address)
    })
  }, [browserLocation, execWithErrorTracking, verifyAndCalculateAddress])

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (window !== window.parent) {
        window.addEventListener('message', onParentMessage)
        parent.postMessage({ type: 'ADDRESS_BOOK.GET_LOCATION' }, '*')
      } else {
        requestLocationIfPossible()
      }
      return () => {
        window.removeEventListener('message', onParentMessage)
        if (permission.current) {
          permission.current.onchange = null
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    verifiedBrowserAddress,
    requestLocation,
    hasReceivedBrowserPermission,
    isLocationBeingResolved,
  }
}
